import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useUser } from '../contexts/UserContext';
import { useTheme } from '../contexts/ThemeContext';
import { 
  Package, 
  ChefHat, 
  Calculator,
  MenuSquare,
  Wine,
  ShoppingCart,
  Menu,
  X,
  ChevronDown,
  Utensils,
  Coffee,
  Shirt,
  UtensilsCrossed,
  GlassWater,
  MapPin,
  User,
  Users,
  UserCog,
  Crown,
  Shield,
  LogOut,
  Settings,
  Moon,
  Sun,
  Camera,
  Thermometer,
  Calendar,
  FileCheck,
  FileText,
  Droplets,
  Sparkles,
  Plus,
  Edit2,
  Eye,
  EyeOff,
  Euro,
  Key,
  LayoutDashboard,
  Briefcase,
  Truck,
  Ruler
} from 'lucide-react';
// Mock notifications removed
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from './ui/dropdown-menu';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { useToast } from '../hooks/use-toast';

const Navbar = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { logout, user: currentUser } = useAuth(); // Get user from AuthContext
  const { toast } = useToast();
  const { theme, toggleTheme } = useTheme(); // Get theme context

  // State variables
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [expandedMobileMenus, setExpandedMobileMenus] = useState({}); // Per sottomenu mobile
  // Notification system removed
  const [isProfileDetailOpen, setIsProfileDetailOpen] = useState(false);
  const [selectedProfile, setSelectedProfile] = useState(null);
  const [isChangePasswordOpen, setIsChangePasswordOpen] = useState(false);
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false
  });

  // Logout function
  const handleLogout = () => {
    logout();
  };

  // Change password function
  const handleChangePassword = () => {
    setIsChangePasswordOpen(true);
  };

  // Submit password change
  const handleSubmitPasswordChange = async () => {
    if (!passwordForm.currentPassword || !passwordForm.newPassword || !passwordForm.confirmPassword) {
      toast({
        variant: "destructive",
        title: "Errore",
        description: "Compila tutti i campi",
      });
      return;
    }

    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      toast({
        variant: "destructive",
        title: "Errore",
        description: "Le nuove password non corrispondono",
      });
      return;
    }

    if (passwordForm.newPassword.length < 6) {
      toast({
        variant: "destructive",
        title: "Errore",
        description: "La nuova password deve essere di almeno 6 caratteri",
      });
      return;
    }

    try {
      // TODO: Implementare API call per cambio password
      // const response = await fetch('/api/change-password', {
      //   method: 'POST',
      //   headers: {
      //     'Content-Type': 'application/json',
      //     'X-User-ID': activeUser.id
      //   },
      //   body: JSON.stringify({
      //     currentPassword: passwordForm.currentPassword,
      //     newPassword: passwordForm.newPassword
      //   })
      // });

      // if (response.ok) {
        // Reset form
        setPasswordForm({
          currentPassword: '',
          newPassword: '',
          confirmPassword: ''
        });
        setIsChangePasswordOpen(false);
      // } else {
      //   toast({
      //     variant: "destructive",
      //     title: "Errore",
      //     description: "Errore durante il cambio password",
      //   });
      // }
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Errore",
        description: "Errore di connessione",
      });
    }
  };

  // Use current user with safe default
  const activeUser = currentUser || { 
    id: 'u1', 
    name: 'Angelo Capuozzo', 
    role: 'Master Admin', 
    avatar: 'AC' 
  };

  // Notification functions removed
  
  // All available navigation items - Ordine: Prodotti, Overview F.C., Ricette, Spesa, Menu Ristorante, Tracciabilità, Haccp, Staff, Utenti
  const allNavigation = [
    { name: 'Prodotti', href: '/prodotti', icon: Package, serviceId: 'prodotti' },
    { name: 'Overview F.C.', href: '/overview', icon: LayoutDashboard, serviceId: 'overview' },
    { name: 'Ricette F.C.', href: '/ricette', icon: ChefHat, serviceId: 'ricette' },
    { name: 'Spesa', href: '/spesa', icon: ShoppingCart, serviceId: 'spesa' },
    { name: 'Menu Ristorante', href: '/menu', icon: MenuSquare, serviceId: 'menu-ristorante' },
    { 
      name: 'Tracciabilità', 
      href: '/tracciabilita', 
      icon: Shield, 
      serviceId: 'tracciabilita',
      hasDropdown: true,
      subItems: [
        { name: 'Lotti', href: '/tracciabilita/lotti', icon: Package },
        { name: 'Piatti', href: '/tracciabilita/piatti', icon: UtensilsCrossed }
      ]
    },
    { 
      name: 'HACCP', 
      href: '/haccp', 
      icon: FileCheck, 
      serviceId: 'haccp',
      hasDropdown: true,
      subItems: [
        { name: 'Frigoriferi', href: '/haccp/frigoriferi', icon: Thermometer },
        { name: 'Olii Esausti', href: '/haccp/olii-esausti', icon: Droplets },
        // Pulizie: nascosta in produzione (adfoodcost.it). Visibile solo in preview/dev.
        ...(typeof window !== 'undefined' && window.location.hostname !== 'adfoodcost.it' && window.location.hostname !== 'www.adfoodcost.it'
          ? [{ name: 'Pulizie', href: '/haccp/pulizie', icon: Sparkles }]
          : [])
      ]
    },
    { 
      name: 'Staff', 
      href: '/staff', 
      icon: UserCog, 
      serviceId: 'staff',
      hasDropdown: true,
      subItems: [
        { name: 'Dipendenti', href: '/staff/dipendenti', icon: Users },
        { name: 'Squadre', href: '/staff/squadre', icon: Briefcase }
      ]
    },
    { name: 'Utenti', href: '/utenti', icon: Users, serviceId: 'utenti' },
  ];

  // Filter navigation based on user's allowedNavItems
  const navigation = allNavigation.filter(item => {
    // Master Admin sees everything
    if (activeUser?.role === 'Master Admin' || 
        activeUser?.profileType === 'MASTER_ADMIN' || 
        activeUser?.email === 'angelocapuozzo85@gmail.com') {
      return true;
    }
    
    // For other users, check allowedNavItems
    // If allowedNavItems doesn't exist or is empty, show nothing (for security)
    if (!activeUser?.allowedNavItems || activeUser.allowedNavItems.length === 0) {
      return false;
    }
    
    // Check if this item is in the user's allowed items
    return activeUser.allowedNavItems.includes(item.serviceId);
  });

  const secondaryNavigation = [];

  // Helper function to check if a navigation item is active (supports sub-routes)
  const isNavItemActive = (item) => {
    switch(item.name) {
      case 'Prodotti':
        return location.pathname.startsWith('/prodotti') || location.pathname === '/ingredients';
      case 'Overview F.C.':
        return location.pathname === '/overview';
      case 'Ricette F.C.':
        return location.pathname.startsWith('/ricette') || location.pathname === '/recipes' || location.pathname === '/beverages';
      case 'Magazzino':
        return location.pathname.startsWith('/magazzino') || location.pathname === '/warehouse';
      case 'Tracciabilità':
        return location.pathname.startsWith('/tracciabilita');
      case 'HACCP':
        return location.pathname.startsWith('/haccp');
      case 'Staff':
        return location.pathname.startsWith('/staff');
      default:
        return location.pathname === item.href;
    }
  };
  
  // Legacy functions for dropdown menus (kept for backward compatibility)
  const isActive = (href) => location.pathname === href;

  const isProductActive = () => location.pathname.startsWith('/prodotti') || location.pathname === '/ingredients';
  const isRecipeActive = () => location.pathname.startsWith('/ricette') || location.pathname === '/recipes' || location.pathname === '/beverages';
  const isWarehouseActive = () => location.pathname.startsWith('/magazzino') || location.pathname === '/warehouse';
  const isTracciabilitaActive = () => location.pathname.startsWith('/tracciabilita');
  const isHaccpActive = () => location.pathname.startsWith('/haccp');

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-slate-900 dark:bg-slate-950 text-white shadow-lg transition-colors duration-200">
      <div className="max-w-full px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo - Now clickable */}
          <div className="flex items-center gap-3 flex-shrink-0">
            <button
              onClick={() => navigate('/prodotti/food')}
              className="flex items-center gap-3 hover:bg-slate-800 dark:hover:bg-slate-900 rounded-lg p-2 transition-colors duration-200 group"
              title="Torna agli articoli"
            >
              <div className="w-8 h-8 bg-gradient-to-br from-orange-500 to-red-600 rounded-lg flex items-center justify-center group-hover:scale-105 transition-transform duration-200">
                <ChefHat className="w-5 h-5 text-white" />
              </div>
              <div className="hidden md:block">
                <h1 className="text-lg font-bold text-white group-hover:text-orange-300 transition-colors duration-200">AD Food Cost</h1>
              </div>
              <div className="block md:hidden">
                <h1 className="text-lg font-bold text-white group-hover:text-orange-300 transition-colors duration-200">FCC</h1>
              </div>
            </button>
          </div>

          {/* Desktop Navigation */}
          <div className="hidden lg:block flex-1 mx-8">
            <div className="flex items-center justify-center space-x-1">
              {navigation.map((item) => {
                const Icon = item.icon;
                
                // Handle dropdowns (Prodotti, Ricette, Tracciabilità, HACCP and Magazzino)
                if (item.submenu) {
                  const isActiveDropdown = item.name === 'Prodotti' ? isProductActive() : 
                                          item.name === 'Ricette F.C.' ? isRecipeActive() :
                                          item.name === 'Tracciabilità' ? isTracciabilitaActive() :
                                          item.name === 'HACCP' ? isHaccpActive() :
                                          item.name === 'Magazzino' ? isWarehouseActive() : false;
                  
                  return (
                    <DropdownMenu key={item.name}>
                      <DropdownMenuTrigger asChild>
                        <button
                          className={`flex items-center gap-2 px-4 py-3 rounded-lg text-base font-medium transition-all duration-200 hover:bg-slate-800 dark:hover:bg-slate-900 whitespace-nowrap ${
                            isActiveDropdown
                              ? 'bg-gradient-to-r from-orange-500 to-red-600 text-white shadow-lg'
                              : 'text-slate-200 dark:text-slate-100 hover:text-white'
                          }`}
                        >
                          <Icon className="w-5 h-5" />
                          <span className="hidden xl:inline">{item.name}</span>
                          <ChevronDown className="w-4 h-4" />
                        </button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="start" className="w-48">
                        {item.submenu.map((subitem) => {
                          const SubIcon = subitem.icon;
                          return (
                            <DropdownMenuItem key={subitem.name} asChild>
                              <Link
                                to={subitem.href}
                                className="flex items-center gap-2 w-full px-2 py-2"
                              >
                                <SubIcon className="w-4 h-4" />
                                {subitem.name}
                              </Link>
                            </DropdownMenuItem>
                          );
                        })}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  );
                }
                
                // Handle new dropdown items (Staff with hasDropdown)
                if (item.hasDropdown && item.subItems) {
                  const isActiveDropdown = isNavItemActive(item);
                  
                  return (
                    <DropdownMenu key={item.name}>
                      <DropdownMenuTrigger asChild>
                        <button
                          className={`flex items-center gap-2 px-4 py-3 rounded-lg text-base font-medium transition-all duration-200 hover:bg-slate-800 dark:hover:bg-slate-900 whitespace-nowrap ${
                            isActiveDropdown
                              ? 'bg-gradient-to-r from-orange-500 to-red-600 text-white shadow-lg'
                              : 'text-slate-200 dark:text-slate-100 hover:text-white'
                          }`}
                        >
                          <Icon className="w-5 h-5" />
                          <span className="hidden xl:inline">{item.name}</span>
                          <ChevronDown className="w-4 h-4" />
                        </button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="start" className="w-48">
                        {item.subItems.map((subitem) => {
                          const SubIcon = subitem.icon;
                          const isSubActive = location.pathname === subitem.href;
                          return (
                            <DropdownMenuItem key={subitem.name} asChild>
                              <Link
                                to={subitem.href}
                                className={`flex items-center gap-2 w-full px-2 py-2 ${isSubActive ? 'bg-orange-100 dark:bg-orange-900/30 text-orange-600 dark:text-orange-400' : ''}`}
                              >
                                <SubIcon className="w-4 h-4" />
                                {subitem.name}
                              </Link>
                            </DropdownMenuItem>
                          );
                        })}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  );
                }
                
                // Handle regular navigation items
                return (
                  <Link
                    key={item.name}
                    to={item.href}
                    className={`flex items-center gap-2 px-4 py-3 rounded-lg text-base font-medium transition-all duration-200 hover:bg-slate-800 dark:hover:bg-slate-900 whitespace-nowrap ${
                      isNavItemActive(item)
                        ? 'bg-gradient-to-r from-orange-500 to-red-600 text-white shadow-lg'
                        : 'text-slate-200 dark:text-slate-100 hover:text-white'
                    }`}
                  >
                    <Icon className="w-5 h-5" />
                    <span className="hidden xl:inline">{item.name}</span>
                  </Link>
                );
              })}
            </div>
          </div>

          {/* Right side items */}
          <div className="flex items-center gap-3">
            {/* Secondary Navigation - Desktop */}
            <div className="hidden lg:flex items-center gap-1">
              {/* Menu Hamburger con Impostazioni e Logout */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    className="flex items-center gap-2 px-4 py-3 rounded-lg text-base font-medium transition-all duration-200 hover:bg-slate-800 dark:hover:bg-slate-900 whitespace-nowrap text-slate-200 dark:text-slate-100 hover:text-white"
                  >
                    <div className="w-5 h-5 border-2 border-white bg-transparent hover:bg-white/20 dark:border-white dark:hover:bg-white/10 shadow-sm rounded-sm flex items-center justify-center">
                      <Menu className="w-3 h-3" />
                    </div>
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuItem asChild>
                    <Link
                      to="/fornitori"
                      className="flex items-center gap-2 w-full px-2 py-2"
                    >
                      <Truck className="w-4 h-4" />
                      Fornitori
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link
                      to="/unita-misura"
                      className="flex items-center gap-2 w-full px-2 py-2"
                    >
                      <Ruler className="w-4 h-4" />
                      Unità di Misura
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link
                      to="/location"
                      className="flex items-center gap-2 w-full px-2 py-2"
                      data-testid="menu-location-link"
                    >
                      <MapPin className="w-4 h-4" />
                      Location
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem>
                    <button
                      onClick={handleChangePassword}
                      className="flex items-center gap-2 w-full px-2 py-2"
                    >
                      <Key className="w-4 h-4" />
                      Cambia password
                    </button>
                  </DropdownMenuItem>
                  <DropdownMenuItem>
                    <button
                      onClick={handleLogout}
                      className="flex items-center gap-2 w-full px-2 py-2 bg-red-600 text-white hover:bg-red-700 rounded-md transition-colors duration-200"
                    >
                      <LogOut className="w-4 h-4" />
                      Logout
                    </button>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
              
              {secondaryNavigation.map((item) => {
                const Icon = item.icon;
                return (
                  <Link
                    key={item.name}
                    to={item.href}
                    className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-colors hover:bg-slate-800 dark:hover:bg-slate-900 ${
                      isActive(item.href)
                        ? 'bg-gradient-to-r from-orange-500 to-red-600 text-white'
                        : 'text-slate-200 dark:text-slate-100 hover:text-white'
                    }`}
                  >
                    <Icon className="w-4 h-4" />
                    <span className="hidden xl:inline">{item.name}</span>
                  </Link>
                );
              })}
            </div>

            {/* Mobile menu button */}
            <button
              className="lg:hidden p-2 rounded-lg text-slate-200 hover:text-white hover:bg-slate-800 dark:hover:bg-slate-900"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            >
              {isMobileMenuOpen ? (
                <X className="w-5 h-5" />
              ) : (
                <Menu className="w-5 h-5" />
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile menu */}
      {isMobileMenuOpen && (
        <div className="lg:hidden border-t border-slate-700 dark:border-slate-800 fixed top-16 left-0 right-0 bottom-0 bg-slate-900 dark:bg-slate-950 z-40 overflow-y-auto">
          <div className="px-2 pt-2 pb-3 space-y-1">
            {navigation.map((item) => {
              const Icon = item.icon;
              
              // Handle Prodotti submenu (collapsible)
              if (item.submenu) {
                const isExpanded = expandedMobileMenus[item.name] || false;
                const isAnySubActive = item.submenu.some(sub => isActive(sub.href));
                
                return (
                  <div key={item.name}>
                    <button
                      onClick={() => setExpandedMobileMenus(prev => ({
                        ...prev,
                        [item.name]: !prev[item.name]
                      }))}
                      className={`flex items-center justify-between w-full px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                        isAnySubActive
                          ? 'bg-orange-500/20 text-orange-400'
                          : 'text-slate-200 dark:text-slate-100 hover:text-white hover:bg-slate-800 dark:hover:bg-slate-900'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <Icon className="w-4 h-4" />
                        {item.name}
                      </div>
                      <ChevronDown className={`w-4 h-4 transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`} />
                    </button>
                    {isExpanded && (
                      <div className="pl-6 space-y-1 mt-1">
                        {item.submenu.map((subitem) => {
                          const SubIcon = subitem.icon;
                          return (
                            <Link
                              key={subitem.name}
                              to={subitem.href}
                              onClick={() => setIsMobileMenuOpen(false)}
                              className={`flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-colors ${
                                isActive(subitem.href)
                                  ? 'bg-gradient-to-r from-orange-500 to-red-600 text-white'
                                  : 'text-slate-200 dark:text-slate-100 hover:text-white hover:bg-slate-800 dark:hover:bg-slate-900'
                              }`}
                            >
                              <SubIcon className="w-4 h-4" />
                              {subitem.name}
                            </Link>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              }
              
              // Handle new dropdown items (Staff, Tracciabilità, HACCP with hasDropdown)
              if (item.hasDropdown && item.subItems) {
                const isExpanded = expandedMobileMenus[item.name] || false;
                const isAnySubActive = item.subItems.some(sub => location.pathname === sub.href);
                
                return (
                  <div key={item.name}>
                    <button
                      onClick={() => setExpandedMobileMenus(prev => ({
                        ...prev,
                        [item.name]: !prev[item.name]
                      }))}
                      className={`flex items-center justify-between w-full px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                        isAnySubActive
                          ? 'bg-orange-500/20 text-orange-400'
                          : 'text-slate-200 dark:text-slate-100 hover:text-white hover:bg-slate-800 dark:hover:bg-slate-900'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <Icon className="w-4 h-4" />
                        {item.name}
                      </div>
                      <ChevronDown className={`w-4 h-4 transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`} />
                    </button>
                    {isExpanded && (
                      <div className="pl-6 space-y-1 mt-1">
                        {item.subItems.map((subitem) => {
                          const SubIcon = subitem.icon;
                          const isSubActive = location.pathname === subitem.href;
                          return (
                            <Link
                              key={subitem.name}
                              to={subitem.href}
                              onClick={() => setIsMobileMenuOpen(false)}
                              className={`flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-colors ${
                                isSubActive
                                  ? 'bg-gradient-to-r from-orange-500 to-red-600 text-white'
                                  : 'text-slate-200 dark:text-slate-100 hover:text-white hover:bg-slate-800 dark:hover:bg-slate-900'
                              }`}
                            >
                              <SubIcon className="w-4 h-4" />
                              {subitem.name}
                            </Link>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              }
              
              // Handle regular navigation items
              return (
                <Link
                  key={item.name}
                  to={item.href}
                  onClick={() => setIsMobileMenuOpen(false)}
                  className={`flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                    isNavItemActive(item)
                      ? 'bg-gradient-to-r from-orange-500 to-red-600 text-white'
                      : 'text-slate-200 dark:text-slate-100 hover:text-white hover:bg-slate-800 dark:hover:bg-slate-900'
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  {item.name}
                </Link>
              );
            })}
            
            <div className="border-t border-slate-700 dark:border-slate-800 pt-2 mt-2">
              
              <Link
                to="/fornitori"
                onClick={() => setIsMobileMenuOpen(false)}
                className={`flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                  location.pathname === '/fornitori'
                    ? 'bg-gradient-to-r from-orange-500 to-red-600 text-white'
                    : 'text-slate-200 dark:text-slate-100 hover:text-white hover:bg-slate-800 dark:hover:bg-slate-900'
                }`}
              >
                <Truck className="w-4 h-4" />
                Fornitori
              </Link>

              <Link
                to="/unita-misura"
                onClick={() => setIsMobileMenuOpen(false)}
                className={`flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                  location.pathname === '/unita-misura'
                    ? 'bg-gradient-to-r from-orange-500 to-red-600 text-white'
                    : 'text-slate-200 dark:text-slate-100 hover:text-white hover:bg-slate-800 dark:hover:bg-slate-900'
                }`}
              >
                <Ruler className="w-4 h-4" />
                Unità di Misura
              </Link>

              <Link
                to="/location"
                onClick={() => setIsMobileMenuOpen(false)}
                className={`flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                  location.pathname === '/location'
                    ? 'bg-gradient-to-r from-orange-500 to-red-600 text-white'
                    : 'text-slate-200 dark:text-slate-100 hover:text-white hover:bg-slate-800 dark:hover:bg-slate-900'
                }`}
              >
                <MapPin className="w-4 h-4" />
                Location
              </Link>

              {secondaryNavigation.map((item) => {
                const Icon = item.icon;
                return (
                  <Link
                    key={item.name}
                    to={item.href}
                    onClick={() => setIsMobileMenuOpen(false)}
                    className={`flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-colors ${
                      isActive(item.href)
                        ? 'bg-gradient-to-r from-orange-500 to-red-600 text-white'
                        : 'text-slate-200 dark:text-slate-100 hover:text-white hover:bg-slate-800 dark:hover:bg-slate-900'
                    }`}
                  >
                    <Icon className="w-4 h-4" />
                    {item.name}
                  </Link>
                );
              })}
              
              <button 
                onClick={handleLogout}
                className="flex items-center gap-3 px-3 py-2 rounded-lg text-sm bg-red-600 text-white hover:bg-red-700 transition-colors w-full text-left"
              >
                <LogOut className="w-4 h-4" />
                Logout
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Profile Details Dialog */}
      <Dialog open={isProfileDetailOpen} onOpenChange={setIsProfileDetailOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <div className="w-8 h-8 bg-gradient-to-r from-orange-500 to-red-600 rounded-full flex items-center justify-center text-white font-bold text-sm">
                {selectedProfile?.avatar || selectedProfile?.name?.substring(0, 2).toUpperCase()}
              </div>
              Dettagli Profilo - {selectedProfile?.name}
            </DialogTitle>
          </DialogHeader>
          
          {selectedProfile && (
            <div className="space-y-4">
              {/* Profile Info */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs font-medium text-gray-500">Nome Completo</Label>
                  <p className="font-medium">{selectedProfile.name}</p>
                </div>
                <div>
                  <Label className="text-xs font-medium text-gray-500">Email</Label>
                  <p className="font-medium text-blue-600">{selectedProfile.email}</p>
                </div>
              </div>
              
              <div className="grid grid-cols-1 gap-4">
                <div>
                  <Label className="text-xs font-medium text-gray-500">Telefono</Label>
                  <p className="font-medium">{selectedProfile.cellulare || 'Non impostato'}</p>
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs font-medium text-gray-500">Tipo Profilo</Label>
                  <p className="font-medium">{selectedProfile.profileType || selectedProfile.role}</p>
                </div>
                <div>
                  <Label className="text-xs font-medium text-gray-500">Status</Label>
                  <div className="flex items-center gap-2">
                    {selectedProfile.isActive !== false ? (
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                        ✅ Attivo
                      </span>
                    ) : (
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                        ❌ Disattivato
                      </span>
                    )}
                  </div>
                </div>
              </div>
              
              {selectedProfile.expirationDate && (
                <div>
                  <Label className="text-xs font-medium text-gray-500">Data Scadenza</Label>
                  <p className="font-medium">{new Date(selectedProfile.expirationDate).toLocaleDateString('it-IT')}</p>
                </div>
              )}
              
              {/* Account Details */}
              <div className="border-t pt-4">
                <Label className="text-xs font-medium text-gray-500">Informazioni Account</Label>
                <div className="mt-2 space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span>ID Utente:</span>
                    <span className="font-mono text-xs text-gray-600">{selectedProfile.id}</span>
                  </div>
                  {selectedProfile.created_at && (
                    <div className="flex justify-between">
                      <span>Creato il:</span>
                      <span>{new Date(selectedProfile.created_at).toLocaleDateString('it-IT')}</span>
                    </div>
                  )}
                  {selectedProfile.updated_at && (
                    <div className="flex justify-between">
                      <span>Ultimo aggiornamento:</span>
                      <span>{new Date(selectedProfile.updated_at).toLocaleDateString('it-IT')}</span>
                    </div>
                  )}
                </div>
              </div>
              
              <div className="flex gap-2 pt-4">
                <Button 
                  variant="outline" 
                  onClick={() => {
                    setIsProfileDetailOpen(false);
                    setSelectedProfile(null);
                  }}
                  className="flex-1"
                >
                  Chiudi
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Dialog Cambia Password */}
      <Dialog open={isChangePasswordOpen} onOpenChange={setIsChangePasswordOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Cambia Password</DialogTitle>
            <DialogDescription>
              Inserisci la tua password attuale e la nuova password
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label htmlFor="currentPassword">Password Attuale *</Label>
              <div className="relative">
                <Input
                  id="currentPassword"
                  type={showPasswords.current ? "text" : "password"}
                  value={passwordForm.currentPassword}
                  onChange={(e) => setPasswordForm({...passwordForm, currentPassword: e.target.value})}
                  placeholder="Inserisci password attuale"
                />
                <button
                  type="button"
                  className="absolute right-2 top-2 p-1 text-gray-400 hover:text-gray-600"
                  onClick={() => setShowPasswords({...showPasswords, current: !showPasswords.current})}
                >
                  {showPasswords.current ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            <div>
              <Label htmlFor="newPassword">Nuova Password *</Label>
              <div className="relative">
                <Input
                  id="newPassword"
                  type={showPasswords.new ? "text" : "password"}
                  value={passwordForm.newPassword}
                  onChange={(e) => setPasswordForm({...passwordForm, newPassword: e.target.value})}
                  placeholder="Inserisci nuova password (min. 6 caratteri)"
                />
                <button
                  type="button"
                  className="absolute right-2 top-2 p-1 text-gray-400 hover:text-gray-600"
                  onClick={() => setShowPasswords({...showPasswords, new: !showPasswords.new})}
                >
                  {showPasswords.new ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            <div>
              <Label htmlFor="confirmPassword">Conferma Nuova Password *</Label>
              <div className="relative">
                <Input
                  id="confirmPassword"
                  type={showPasswords.confirm ? "text" : "password"}
                  value={passwordForm.confirmPassword}
                  onChange={(e) => setPasswordForm({...passwordForm, confirmPassword: e.target.value})}
                  placeholder="Conferma la nuova password"
                />
                <button
                  type="button"
                  className="absolute right-2 top-2 p-1 text-gray-400 hover:text-gray-600"
                  onClick={() => setShowPasswords({...showPasswords, confirm: !showPasswords.confirm})}
                >
                  {showPasswords.confirm ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => {
                setIsChangePasswordOpen(false);
                setPasswordForm({
                  currentPassword: '',
                  newPassword: '',
                  confirmPassword: ''
                });
                setShowPasswords({
                  current: false,
                  new: false,
                  confirm: false
                });
              }}
            >
              Annulla
            </Button>
            <Button onClick={handleSubmitPasswordChange}>
              Cambia Password
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

    </nav>
  );
};

export default Navbar;